<template>
  <div
    v-if="displayReading"
    class="rounded bg-stone-50 p-3 flex flex-col justify-between border-stone-100 border-3"
    :class="{ 'opacity-50 animate-pulse': isLoading }"
  >
    <div pb-3 :class="{ invisible: isLoading }">
      <div text-stone-600 text-lg lh-snug font-semibold>
        {{ displayReading.station_name }}
      </div>
      <div
        flex
        flex-wrap
        text-stone-600
        font-mono
        text-sm
        pb-4
        :class="{ 'gap-5 sm:(justify-between gap-2)': !isRecent }"
      >
        <div :class="{ 'after:(content-[\'•\'] last:content-none mx-2)': isRecent }">
          {{ round(displayReading.distance, 0) }}&nbsp;mi&nbsp;{{ displayReading.direction }}
        </div>
        <div v-if="readingDate(displayReading)" :title="readingDate(displayReading).toLocaleString()">
          <UseTimeAgo v-slot="{ timeAgo }" :time="readingDate(displayReading)" v-if="isRecent">
            {{ timeAgo }}
          </UseTimeAgo>
          <span v-else>{{ formattedDate(readingDate(displayReading)) }}</span>
        </div>
      </div>
      <div flex gap-8>
        <div v-if="isNumber(displayReading.snowfall_inches)">
          <div text-3xl font-semibold text-stone-700 tabular-nums>{{ displayReading.snowfall_inches }}&nbsp;in</div>
          <div font-mono text text-stone-500 lh-snug>
            snowfall ({{ displayReading.snowfall_duration_hours }}&nbsp;hrs)
          </div>
        </div>
        <div v-if="isNumber(displayReading.snow_depth_inches)">
          <div text-3xl font-semibold text-stone-700 tabular-nums>{{ displayReading.snow_depth_inches }}&nbsp;in</div>
          <div font-mono text text-stone-500 lh-snug>snow depth</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { UseTimeAgo } from "@vueuse/components";

const props = defineProps({
  reading: {
    type: Object,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const isLoading = computed(() => props.loading && !props.reading);

const displayReading = computed(() => {
  if (isLoading.value) {
    return {
      station_name: "\u00a0",
      distance: 0,
      direction: "\u00a0",
      snowfall_inches: 0,
      snowfall_duration_hours: 24,
      snow_depth_inches: 0,
      snow_depth_date: toISODate(new Date()),
      snowfall_date: toISODate(new Date()),
    };
  }
  if (!isNumber(props.reading?.snowfall_inches) && !isNumber(props.reading?.snow_depth_inches)) {
    return null;
  }
  return props.reading;
});

const isRecent = computed(() => {
  const date = readingDate(displayReading.value);
  if (!date) {
    return false;
  }

  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 1);

  return date > twoDaysAgo;
});

function readingDate(reading) {
  if (reading?.snow_depth_date) {
    return new Date(reading.snow_depth_date);
  }
  if (reading?.snowfall_date) {
    return new Date(reading?.snowfall_date);
  }
  return null;
}

function formattedDate(date) {
  const isThisYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: isThisYear ? undefined : "numeric",
    hour: isThisYear ? "numeric" : undefined,
    minute: isThisYear ? "numeric" : undefined,
  });
}
</script>
